import { createSharedComposable } from '@vueuse/core';

// eslint-disable-next-line regex/invalid
import { SdpLeadModal } from '#components';
import { type BackendErrorEntity, type ErrorType, LeadIntent, type PostLeadBodyDto, SellingMethod, useApiPostLead } from '~/apiClient';
import type { LeadModalProps } from '~/components/Sdp/SdpLeadModal.vue';
import type { SaleItemEntity } from '~/types/sale.type';

export type CustomerLeadContacts = {
    email: string;
    phone: string;
};

type UseLeadModalReturn = {
    captureLeadContacts: (params: LeadModalProps) => Promise<CustomerLeadContacts | undefined>;
    closeLeadModal: VoidFunction;
};

const STORAGE_KEY = 'lead_contacts';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const _useSdpLead = (): UseLeadModalReturn => {
    // COMPOSABLES
    const { data: session, isLoggedIn } = useAuthUtils();
    const { mutateAsync: mutateSubmit } = useApiPostLead();
    const { locale } = useTypedI18n();
    const route = useRoute();
    const { logError } = useLogs();
    const cachedContacts = useLocalStorage<{ data: CustomerLeadContacts; ts: number } | null>(STORAGE_KEY, null);
    const overlay = useOverlay();
    const leadModal = overlay.create(SdpLeadModal);

    // METHODS
    const getCached = (): CustomerLeadContacts | null => {
        if (!cachedContacts.value) return null;

        if (Date.now() - cachedContacts.value.ts > TTL_MS) {
            cachedContacts.value = null;

            return null;
        }

        return cachedContacts.value.data;
    };

    const postLead = async (contacts: CustomerLeadContacts, sale: SaleItemEntity, intentType: LeadIntent): Promise<void> => {
        const body: PostLeadBodyDto = {
            ...{ [sale.sellingMethod === SellingMethod.Auction ? 'auctionId' : 'directSaleId']: sale.id },
            ...contacts,
            intentType,
            language: localeToLanguage(locale.value),
            urlPath: route.fullPath,
        };

        await mutateSubmit({
            data: body,
        }).catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error posting user lead Data', { userId: session.value?.user, body, apiError });
        });
    };

    const captureLeadContacts: UseLeadModalReturn['captureLeadContacts'] = async ({ sale, intentType }) => {
        const { phone, email } = session.value?.user ?? {};

        if (isLoggedIn.value && phone && email) {
            await postLead({ email, phone }, sale, intentType);

            return { email, phone };
        }

        const cached = getCached();

        if (cached) {
            await postLead(cached, sale, intentType);

            return cached;
        }

        const guestContacts = await leadModal.open({ sale, intentType });
        if (guestContacts) cachedContacts.value = { data: guestContacts, ts: Date.now() };

        return guestContacts;
    };

    return {
        captureLeadContacts,
        closeLeadModal: () => leadModal.close(),
    };
};

export const useSdpLead = createSharedComposable(_useSdpLead);
